/**
 * Banners anclados tras inscripción a clase en vivo.
 * Persisten hasta que pasa end_time (o start_time + 60 min).
 */

const STORAGE_KEY = 'd28d.live_class.enroll_banners';

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch { /* noop */ }
}

function notifyChanged() {
  try {
    window.dispatchEvent(new CustomEvent('d28d:enroll-banner:changed'));
  } catch { /* noop */ }
}

export function resolveClassEndMs(classItem) {
  const end = classItem?.end_time ? new Date(classItem.end_time).getTime() : NaN;
  if (Number.isFinite(end)) return end;
  const start = classItem?.start_time ? new Date(classItem.start_time).getTime() : NaN;
  if (Number.isFinite(start)) return start + 60 * 60 * 1000;
  return Date.now() + 60 * 60 * 1000;
}

export function buildEnrollSuccessMessage(classTitle) {
  const name = String(classTitle || 'esta clase').trim() || 'esta clase';
  return `Excelente, has quedado registrado para la clase ${name}. Recuerda conectarte 5 minutos antes, nos vemos en clase.`;
}

export function rememberEnrollBanner({ userId, classId, classTitle, endMs }) {
  if (userId == null || classId == null) return;
  const expiresAt = Number(endMs) || Date.now() + 60 * 60 * 1000;
  if (expiresAt <= Date.now()) return;
  const next = readAll().filter((b) => {
    if (Number(b.expiresAt) <= Date.now()) return false;
    if (Number(b.userId) === Number(userId) && Number(b.classId) === Number(classId)) return false;
    return true;
  });
  next.push({
    userId: Number(userId),
    classId: Number(classId),
    classTitle: String(classTitle || '').trim(),
    expiresAt,
    createdAt: Date.now(),
  });
  writeAll(next);
  notifyChanged();
}

/**
 * Si el usuario ya está inscrito en clases abiertas, asegura el banner
 * (cubre toast perdido, refresh o localStorage vacío).
 */
export function syncEnrollBannersFromClasses(userId, classes = []) {
  if (userId == null) return;
  let changed = false;
  const existing = readAll();
  const byKey = new Set(
    existing
      .filter((b) => Number(b.userId) === Number(userId) && Number(b.expiresAt) > Date.now())
      .map((b) => Number(b.classId)),
  );
  for (const c of classes) {
    const ids = Array.isArray(c?.enrolled_user_ids) ? c.enrolled_user_ids : [];
    if (!ids.some((id) => Number(id) === Number(userId))) continue;
    const endMs = resolveClassEndMs(c);
    if (endMs <= Date.now()) continue;
    const cid = Number(c.id);
    if (!Number.isFinite(cid) || byKey.has(cid)) continue;
    existing.push({
      userId: Number(userId),
      classId: cid,
      classTitle: String(c.title || '').trim(),
      expiresAt: endMs,
      createdAt: Date.now(),
    });
    byKey.add(cid);
    changed = true;
  }
  const pruned = existing.filter((b) => Number(b.expiresAt) > Date.now());
  if (changed || pruned.length !== existing.length) {
    writeAll(pruned);
    notifyChanged();
  }
}

export function listActiveEnrollBanners(userId) {
  const now = Date.now();
  const all = readAll();
  const active = all.filter(
    (b) => Number(b.userId) === Number(userId) && Number(b.expiresAt) > now,
  );
  if (active.length !== all.length) {
    writeAll(active.concat(all.filter((b) => Number(b.userId) !== Number(userId) && Number(b.expiresAt) > now)));
  }
  return active.sort((a, b) => Number(a.expiresAt) - Number(b.expiresAt));
}

export function dismissEnrollBanner(userId, classId) {
  writeAll(readAll().filter(
    (b) => !(Number(b.userId) === Number(userId) && Number(b.classId) === Number(classId)),
  ));
  notifyChanged();
}
